<script lang="ts" setup>
import { useDateTime } from '@ai/ui-components'
import { computed, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  formatChartCategories,
  getCurrentHighlightIndex,
  localizeChartUnits,
} from '@/utils/chart-labels'
import { useColdMonitoringApi } from '@/composables/api/useColdMonitoringApi'
import type { DateRange } from '@/composables/home/useHomeDateRange.ts'
import type { ChartProps } from '@/types/Charts.ts'

const { t } = useI18n({ useScope: 'global' })
const { formatDateTime } = useDateTime()

const props = defineProps<{
  dateRange: DateRange
}>()

const emit = defineEmits<{
  error: [error: Error]
}>()

const { fetchColdChart, loading } = useColdMonitoringApi({
  period: ref(undefined),
  agents: ref(undefined),
})

const chartData = ref<ChartProps>({
  chartType: 'bar',
  series: [],
  loading: false,
})
const error = ref<Error | null>(null)
const dateRangeRef = toRef(props, 'dateRange')

const formatAbsoluteLabel = (date: Date) =>
  formatDateTime({ format: 'chartHourTick', value: date, fallback: '' })

const formattedCategories = computed(() => {
  if (!chartData.value.categories) return chartData.value.categories

  return formatChartCategories(chartData.value.categories, 'hour', {
    t,
    formatAbsoluteLabel,
    useCurrentLabels: false,
  })
})

const localizedSeries = computed(() => localizeChartUnits(chartData.value.series, t))

const loadChart = async () => {
  if (!props.dateRange.datetimeFrom || !props.dateRange.datetimeTo) {
    return
  }

  try {
    error.value = null

    const coldMonitoringChartsResponse = await fetchColdChart('total-calls', {
      datetimeFrom: props.dateRange.datetimeFrom,
      datetimeTo: props.dateRange.datetimeTo,
      chartInterval: 'hour',
    })
    if (
      coldMonitoringChartsResponse?.data[0] !== undefined &&
      coldMonitoringChartsResponse.data[0] !== null
    ) {
      chartData.value = coldMonitoringChartsResponse.data[0] as ChartProps
    }
  } catch (err) {
    error.value = err instanceof Error ? err : new Error('Failed to load chart')
    emit('error', error.value)
  }
}

watch(
  dateRangeRef,
  (newVal, oldVal) => {
    // Only load if values actually changed
    if (newVal.datetimeFrom !== oldVal?.datetimeFrom || newVal.datetimeTo !== oldVal.datetimeTo) {
      void loadChart()
    }
  },
  { immediate: true, deep: true },
)
const highlightBarIndex = computed(() =>
  getCurrentHighlightIndex(chartData.value.categories ?? [], 'hour'),
)
const highlightBar = computed<ChartProps['highlightBar']>(() =>
  typeof highlightBarIndex.value === 'number' ? 'index' : 'none',
)
</script>

<template>
  <OChart
    v-if="!error"
    chartClass="min-h-0 h-full"
    chartType="bar"
    period="hour"
    :title="t('home.calls_over_time')"
    :categories="formattedCategories"
    :rawCategories="chartData.categories"
    :colors="['#6657EB']"
    :series="localizedSeries"
    :loading="loading"
    :highlightBar="highlightBar"
    :highlightBarIndex="highlightBarIndex"
    :displayLegend="false"
  />
</template>
